import { Badge, Box, Card, Column, Pagination, Table } from '@palmetto/palmetto-components';
import { useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { ProgramType, ProgramTypeLabels } from 'types';

import { useGetPriceSheetMappingBulkJobsQuery } from '../../../../services/pricing';
import DateTimeDisplay from '../../../DateTime';
import { TableLoading } from '../../../TableLoading';

const PAGE_SIZE = 20;
const ACTIVE_REFRESH_MS = 5000;
const IDLE_REFRESH_MS = 30_000;
const TABLE_SCROLLABLE = { x: true } as const;

interface BulkJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  programType: ProgramType;
  total: number;
  processed: number;
  error?: string;
  requestedBy?: string;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
}

const STATUS_VARIANT: Record<BulkJob['status'], 'info' | 'warning' | 'success' | 'danger'> = {
  pending: 'info',
  processing: 'warning',
  completed: 'success',
  failed: 'danger',
};

const StatusBadge = ({ status }: { status: BulkJob['status'] }) => (
  <Badge variant={STATUS_VARIANT[status]} message={status} />
);

const Progress = ({ processed, total }: { processed: number; total: number }) => {
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return (
    <Box fontSize="sm">
      {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
    </Box>
  );
};

const TimestampCell = ({ value }: { value?: string }) =>
  value ? <DateTimeDisplay value={value} fontSize="sm" /> : <Box color="body-secondary">—</Box>;

export const BulkJobs = () => {
  const { programType = ProgramType.solar } = useParams<{ programType: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageNum = Number(searchParams.get('page')) || 1;

  // First subscription returns the data; the second subscription drives polling
  // at a faster cadence when there are in-flight jobs. RTK Query dedupes the
  // underlying request so this is just two cache subscriptions on the same key.
  const { data, isLoading, isFetching } = useGetPriceSheetMappingBulkJobsQuery({
    programType: programType as ProgramType,
    pageNum,
    pageSize: PAGE_SIZE,
  });
  const hasActiveJobs = data?.data?.some((job) => job.status === 'pending' || job.status === 'processing');
  useGetPriceSheetMappingBulkJobsQuery(
    { programType: programType as ProgramType, pageNum, pageSize: PAGE_SIZE },
    { pollingInterval: hasActiveJobs ? ACTIVE_REFRESH_MS : IDLE_REFRESH_MS },
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      searchParams.set('page', newPage.toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const columns: Column[] = useMemo(
    () => [
      {
        heading: 'Status',
        dataKey: 'status',
        render: (_cell: any, row: any) => <StatusBadge status={row.status} />,
        width: 120,
      },
      {
        heading: 'Progress',
        dataKey: 'processed',
        render: (_cell: any, row: any) => <Progress processed={row.processed} total={row.total} />,
        width: 180,
      },
      {
        heading: 'Program',
        dataKey: 'programType',
        render: (_cell: any, row: any) => <Box fontSize="sm">{row.programType}</Box>,
        width: 140,
      },
      {
        heading: 'Requested By',
        dataKey: 'requestedBy',
        render: (_cell: any, row: any) =>
          row.requestedBy ? <Box fontSize="sm">{row.requestedBy}</Box> : <Box color="body-secondary">—</Box>,
        width: 200,
      },
      {
        heading: 'Created',
        dataKey: 'createdAt',
        render: (_cell: any, row: any) => <TimestampCell value={row.createdAt} />,
        width: 200,
      },
      {
        heading: 'Started',
        dataKey: 'startedAt',
        render: (_cell: any, row: any) => <TimestampCell value={row.startedAt} />,
        width: 200,
      },
      {
        heading: 'Completed',
        dataKey: 'completedAt',
        render: (_cell: any, row: any) => <TimestampCell value={row.completedAt} />,
        width: 200,
      },
      {
        heading: 'Error',
        dataKey: 'error',
        render: (_cell: any, row: any) =>
          row.error ? (
            <Box fontSize="sm" color="danger">
              {row.error}
            </Box>
          ) : (
            <Box color="body-secondary">—</Box>
          ),
      },
    ],
    [],
  );

  // Table requires a stable `id` per row; the API uses `jobId`. Build the
  // shape with explicit fields rather than spreading to avoid the
  // map-spread allocation lint and to keep ownership of the row shape clear.
  const rows = useMemo(
    () =>
      (data?.data ?? []).map((job) => ({
        id: job.jobId,
        jobId: job.jobId,
        status: job.status,
        programType: job.programType,
        total: job.total,
        processed: job.processed,
        error: job.error,
        requestedBy: job.requestedBy,
        createdAt: job.createdAt,
        startedAt: job.startedAt,
        completedAt: job.completedAt,
      })),
    [data?.data],
  );
  const total = data?.total ?? 0;

  return (
    <Card padding="sm">
      <Card.Header>
        <Box direction="row" justifyContent="space-between" alignItems="center">
          <Box gap="2xs">
            <Box fontSize="sm" color="body-primary">
              <Link to={`/admin/pricing/${programType}`}>Price Mappings</Link>
            </Box>
            <Box fontSize="2xl">Bulk Update Jobs</Box>
            <Box fontSize="sm" color="body-secondary">
              Bulk price sheet mapping updates for {ProgramTypeLabels[programType as ProgramType] ?? programType},
              newest first.
            </Box>
          </Box>
        </Box>
      </Card.Header>
      <Box padding="sm">
        {isLoading ? (
          <TableLoading rows={8} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            rows={rows}
            isLoading={isFetching}
            isScrollable={TABLE_SCROLLABLE}
            hasStickyHeader
            emptyCellPlaceholder="—"
          />
        )}
      </Box>
      {total > 0 && (
        <Pagination
          activePage={pageNum}
          arePagesVisible
          itemsPerPage={PAGE_SIZE}
          numberOfPagesDisplayed={3}
          onChange={onPageChange}
          totalItemsCount={total}
          isCompact
        />
      )}
    </Card>
  );
};
